import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LockIcon from '@mui/icons-material/Lock';
import {
  RED_SCROLLBAR_SX,
  FilterApplyButton,
  FILTER_BAR_PAPER_SX,
  FILTER_FIELD_SX,
  FILTER_LABEL_SX,
  FILTER_BAR_GAP,
  FILTER_BUTTON_SX,
} from '../../Common/DashboardKit';
import { DEFAULT_EFFORTS_ROLE } from '../hooks/useEffortsFilters.js';
import {
  useEffortsFilterOptions,
  useEffortsFilterState,
  useEffortsPeriods,
} from '../hooks/useEffortsDashboard.js';
import { useUrlFilters } from '../../../hooks/useUrlFilters.js';

/** Show the in-list search box once a list has more than this many options. */
const SEARCH_THRESHOLD = 8;

// ── Reference values copied from the Sales dashboard (SalesPulse) ────────────
// Source: semantic_friday_frontend-semantic_prod/src/components/SalesDashboard
//   components/AsyncFilterDropdown.jsx + Dashboard.css (.sd-dropdown*, .sd-badge).
// Exact hex/size/weight/radius values from that CSS, not estimated from images.
const SD = {
  // Has-selection header: near-black text (NOT blue), weight 600, light-blue
  // fill, blue border (.sd-dropdown__header--active).
  activeText: '#0F172A',
  activeBorder: '#1D4ED8',
  activeFill: '#EFF6FF',
  idleText: '#94A3B8', // placeholder text (.sd-dropdown__header)
  idleBorder: '#E2E8F0',
  valueText: '#0F172A',
  // Count chip (.sd-dropdown__count)
  chipBg: '#F1F5F9',
  chipBorder: '#E2E8F0',
  chipText: '#94A3B8',
  // Option rows
  itemTitle: '#0F172A',
  itemDivider: '#F8FAFC',
  selectedBg: '#EFF6FF',
  selectedText: '#1D4ED8',
  metaText: '#64748B',
  rowHover: '#F8FAFC',
  // Scrollbar (.sd-dropdown__list::-webkit-scrollbar) — thin grey, 4px
  scrollThumb: '#CBD5E1',
  // Search focus (.sd-dropdown__search:focus)
  searchBorder: '#E2E8F0',
  searchFocus: '#1D4ED8',
  searchBg: '#F8FAFC',
};

// Achievement badge colours (.sd-badge--positive/warning/negative). The BA
// overrides the reference thresholds for Efforts coverage: green >=90,
// amber 85-90, red <85 (reference used 90/80). Palette hexes are the reference.
const SD_BADGE = {
  positive: { color: '#16A34A', bg: '#DCFCE7' },
  warning: { color: '#EA580C', bg: '#FFEDD5' },
  negative: { color: '#DC2626', bg: '#FEE2E2' },
};

/** Coverage → badge tone per the BA thresholds (>=90 green, 85-90 amber, <85 red). */
function coverageTone(coveragePercent) {
  const v = Number(coveragePercent);
  if (!Number.isFinite(v)) return null;
  if (v >= 90) return 'positive';
  if (v >= 85) return 'warning';
  return 'negative';
}

/** Dropdown panel: white, 10px radius, soft shadow, app-wide red scrollbar. */
const MENU_PAPER_SX = {
  maxHeight: 280,
  borderRadius: '10px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
  ...RED_SCROLLBAR_SX,
};

/**
 * Closed-select style matching .sd-dropdown__header / --active. Has-selection
 * shows light-blue fill + blue border, but the TEXT stays near-black weight 600
 * (never blue) — Year/Period/Division all share this.
 */
const selectSx = (hasSelection) => ({
  borderRadius: '8px',
  bgcolor: hasSelection ? SD.activeFill : 'background.paper',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: hasSelection ? SD.activeBorder : SD.idleBorder,
    borderWidth: '1.5px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: hasSelection ? SD.activeBorder : '#94A3B8',
  },
  '& .MuiSelect-select': {
    fontWeight: 600,
    color: hasSelection ? SD.activeText : SD.idleText,
    fontSize: '13px',
  },
});

/**
 * Brand / BDM / ZM have no data yet, so they are hidden. The locked-filter code
 * is kept behind this flag so they are trivial to restore once data lands.
 */
const SHOW_LOCKED_FILTERS = false;

/** Static designation option values (label == value). */
const ROLE_OPTIONS = ['FLS', 'FLM', 'SLM', 'TLM'];

/** Static tenure-band option values and their display labels. */
const TENURE_OPTIONS = ['lt6m', '6to12m', '1to3y', 'gt3y'];
const TENURE_LABELS = {
  lt6m: '< 6 months',
  '6to12m': '6-12 months',
  '1to3y': '1-3 years',
  gt3y: '> 3 years',
};

// ── URL ownership ────────────────────────────────────────────────────────────
// The Efforts page OWNS exactly these query-param keys. The URL carries month
// as YYYY-MM (shared vocabulary with the other dashboards); the store/wire use
// YYYYMM, so the two month helpers below convert ONLY at the URL boundary — the
// wire format is never changed. Multi-select values (division/state/hq/role/
// tenure) ride as comma-separated lists, matching the existing API contract
// (EffortsApi joins these same lists with commas), so option values never
// contain a comma. There is no `applied=1` marker; Efforts has its own Apply.
const EFFORTS_FILTER_KEYS = ['fy', 'month', 'division', 'state', 'hq', 'role', 'tenure'];

/** URL month "YYYY-MM" → wire/store "YYYYMM". Passes through anything else. */
function monthUrlToWire(ym) {
  if (!ym) return null;
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym));
  return m ? `${m[1]}${m[2]}` : String(ym);
}

/** Wire/store month "YYYYMM" → URL "YYYY-MM". Passes through anything else. */
function monthWireToUrl(ym) {
  if (!ym) return null;
  const m = /^(\d{4})(\d{2})$/.exec(String(ym));
  return m ? `${m[1]}-${m[2]}` : String(ym);
}

/** Split a comma-separated URL list into a trimmed, non-empty string[]. */
function splitList(raw) {
  if (!raw) return [];
  return String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Join a string[] into a comma-separated URL value (null when empty). */
function joinList(list) {
  const arr = (list ?? []).filter(Boolean);
  return arr.length ? arr.join(',') : null;
}

/** Inline count chip rendered right after a filter label (navy, white, 99+ cap). */
function CountChip({ count }) {
  if (!count) return null;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 17,
        height: 15,
        px: '4px',
        borderRadius: '8px',
        bgcolor: SD.chipBg,
        border: '1px solid',
        borderColor: SD.chipBorder,
        color: SD.chipText,
        fontSize: '9px',
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {count > 99 ? '99+' : count}
    </Box>
  );
}

/** Uppercase caption label with optional inline count chip and lock icon. */
function FilterLabel({ label, count, locked }) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Typography sx={FILTER_LABEL_SX}>
        {label}
      </Typography>
      {locked ? <LockIcon sx={{ fontSize: 13, color: 'text.disabled' }} /> : null}
      <CountChip count={count} />
    </Box>
  );
}

// Shared field wrapper — one source of truth across all filter bars.
const FIELD_SX = FILTER_FIELD_SX;

/**
 * Option-row content: a bold primary name, an optional muted secondary line,
 * and an optional right-aligned pill. Efforts options are plain name strings
 * today, so `secondary` and `pill` are absent and only the name renders — no
 * placeholder, no invented numbers. The slots exist so that if the options are
 * ever enriched (e.g. `{ label, secondary, pill: { text, tone } }`) the layout
 * is already in place. `tone` → green/red pill colour.
 */
function OptionRow({ primary, secondary, pill, selected }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        width: '100%',
        minWidth: 0,
      }}
    >
      <Typography
        noWrap
        sx={{
          fontWeight: 600,
          fontSize: '12.5px',
          color: selected ? SD.selectedText : SD.itemTitle,
        }}
      >
        {primary}
      </Typography>
      {secondary || (pill && pill.text) ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mt: '2px',
          }}
        >
          {secondary ? (
            <Typography noWrap sx={{ fontSize: '11px', color: SD.metaText }}>
              {secondary}
            </Typography>
          ) : (
            <span />
          )}
          {pill && pill.text ? (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                fontSize: '10px',
                fontWeight: 700,
                padding: '1px 5px',
                borderRadius: '4px',
                color: SD_BADGE[pill.tone]?.color,
                bgcolor: SD_BADGE[pill.tone]?.bg,
              }}
            >
              {pill.text}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * Build the secondary line + pill for an option from its stats entry. Returns
 * nulls when no stats exist for the option, so nothing extra renders (no
 * invented numbers). Stats shape (per backend): { flsCount, avgWorkingDays,
 * coveragePercent }.
 */
function statsMeta(stat) {
  if (!stat || typeof stat !== 'object') return { secondary: null, pill: null };
  const parts = [];
  if (stat.flsCount != null) parts.push(`${stat.flsCount} FLS`);
  if (stat.avgWorkingDays != null) parts.push(`Avg WD ${Number(stat.avgWorkingDays).toFixed(1)}`);
  const secondary = parts.length ? parts.join(' · ') : null;
  const tone = stat.coveragePercent != null ? coverageTone(stat.coveragePercent) : null;
  const pill = tone
    ? { text: `Coverage ${Number(stat.coveragePercent).toFixed(1)}%`, tone }
    : null;
  return { secondary, pill };
}

/**
 * A single multi-select checkbox dropdown with Select all / Clear, an optional
 * in-list search box, and an inline selected-count chip on the label.
 */
function MultiSelectFilter({
  label,
  value,
  options,
  onChange,
  onClose,
  loading = false,
  getOptionLabel = (o) => o,
  stats = null,
}) {
  const [search, setSearch] = useState('');
  // Preserve any selected values that aren't in the option list yet, so
  // selections survive before options load and remain toggleable.
  const allOptions = useMemo(() => {
    const extra = value.filter((v) => !options.includes(v));
    return [...extra, ...options];
  }, [options, value]);

  const showSearch = allOptions.length > SEARCH_THRESHOLD;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allOptions;
    return allOptions.filter((o) => getOptionLabel(o).toLowerCase().includes(q));
  }, [allOptions, search, getOptionLabel]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const toggle = (option) => {
    if (selectedSet.has(option)) {
      onChange(value.filter((v) => v !== option));
    } else {
      onChange([...value, option]);
    }
  };

  const selectAll = () => onChange([...new Set([...value, ...filtered])]);
  const clear = () => onChange([]);

  return (
    <Box sx={FIELD_SX}>
      <FilterLabel label={label} count={value.length} />
      <FormControl size="small" fullWidth>
        <Select
          multiple
          displayEmpty
          value={value}
          onOpen={() => setSearch('')}
          onClose={() => onClose?.()}
          onChange={() => {}}
          sx={selectSx(value.length > 0)}
          renderValue={(selected) =>
            selected.length === 0 ? (
              <Box component="span" sx={{ color: SD.idleText }}>
                All
              </Box>
            ) : selected.length === 1 ? (
              getOptionLabel(selected[0])
            ) : (
              `${selected.length} selected`
            )
          }
          endAdornment={
            loading ? (
              <InputAdornment position="end" sx={{ mr: 2 }}>
                <CircularProgress size={16} aria-label={`Loading ${label} options`} />
              </InputAdornment>
            ) : null
          }
          MenuProps={{ PaperProps: { sx: MENU_PAPER_SX }, autoFocus: false }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              px: 1,
              py: 0.5,
            }}
          >
            <Button
              size="small"
              onClick={selectAll}
              disabled={filtered.length === 0}
              sx={{ fontWeight: 600, color: SD.selectedText }}
            >
              Select all
            </Button>
            <Button
              size="small"
              onClick={clear}
              disabled={value.length === 0}
              sx={{ fontWeight: 600, color: SD.metaText }}
            >
              Clear
            </Button>
          </Box>

          {showSearch ? (
            <ListSubheader sx={{ p: 1, bgcolor: 'background.paper' }}>
              <TextField
                size="small"
                fullWidth
                autoFocus
                placeholder={`Search ${label.toLowerCase()}…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                sx={{
                  '& .MuiOutlinedInput-root': { bgcolor: SD.searchBg },
                  '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                    borderColor: SD.searchBorder,
                  },
                  '& .MuiOutlinedInput-root.Mui-focused': { bgcolor: '#fff' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: SD.searchFocus,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </ListSubheader>
          ) : null}

          <Divider />

          {loading && allOptions.length === 0 ? (
            <MenuItem disabled>
              <em>Loading…</em>
            </MenuItem>
          ) : null}

          {!loading && filtered.length === 0 ? (
            <MenuItem disabled>
              <em>No matches</em>
            </MenuItem>
          ) : null}

          {filtered.map((option) => {
            const isSelected = selectedSet.has(option);
            const meta = stats ? statsMeta(stats[option]) : { secondary: null, pill: null };
            return (
              <MenuItem
                key={option}
                value={option}
                onClick={() => toggle(option)}
                dense
                sx={{
                  alignItems: 'flex-start',
                  borderBottom: '1px solid',
                  borderColor: SD.itemDivider,
                  bgcolor: isSelected ? SD.selectedBg : 'transparent',
                  '&:hover': { bgcolor: isSelected ? SD.selectedBg : SD.rowHover },
                }}
              >
                <Checkbox checked={isSelected} size="small" sx={{ py: 0, mr: 0.5, mt: '1px' }} />
                <OptionRow
                  primary={getOptionLabel(option)}
                  secondary={meta.secondary}
                  pill={meta.pill}
                  selected={isSelected}
                />
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Box>
  );
}

/** A single-value dropdown (Year / Period / Designation / Tenure). */
function SingleSelectFilter({ label, value, options, onChange, loading = false, placeholder = 'All' }) {
  return (
    <Box sx={FIELD_SX}>
      <FilterLabel label={label} />
      <FormControl size="small" fullWidth>
        <Select
          displayEmpty
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
          sx={selectSx(value != null && value !== '')}
          renderValue={(v) => {
            if (v === '' || v == null) {
              return (
                <Box component="span" sx={{ color: SD.idleText }}>
                  {placeholder}
                </Box>
              );
            }
            return options.find((o) => String(o.value) === String(v))?.label ?? v;
          }}
          endAdornment={
            loading ? (
              <InputAdornment position="end" sx={{ mr: 2 }}>
                <CircularProgress size={16} aria-label={`Loading ${label} options`} />
              </InputAdornment>
            ) : null
          }
          MenuProps={{ PaperProps: { sx: MENU_PAPER_SX } }}
        >
          {options.map((o) => {
            const isSelected = String(o.value ?? '') === String(value ?? '');
            return (
              <MenuItem
                key={String(o.value ?? 'all')}
                value={o.value ?? ''}
                sx={{
                  fontWeight: 600,
                  fontSize: '12.5px',
                  color: isSelected ? SD.selectedText : SD.itemTitle,
                  bgcolor: isSelected ? SD.selectedBg : 'transparent',
                  '&:hover': { bgcolor: isSelected ? SD.selectedBg : SD.rowHover },
                  '&.Mui-selected': { bgcolor: SD.selectedBg },
                  '&.Mui-selected:hover': { bgcolor: SD.selectedBg },
                }}
              >
                {o.label}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Box>
  );
}

/** A permanently locked (disabled) filter placeholder — no data available. */
function LockedFilter({ label }) {
  return (
    <Box sx={FIELD_SX}>
      <FilterLabel label={label} locked />
      <FormControl size="small" fullWidth disabled>
        <Select value="" displayEmpty renderValue={() => <em>All</em>}>
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
}

/** Shallow set-equality for two string arrays. */
function sameSelection(a, b) {
  if (a.length !== b.length) return false;
  const bSet = new Set(b);
  return a.every((v) => bSet.has(v));
}

const emptyDraft = () => ({
  year: null,
  yearmonth: null,
  division: [],
  state: [],
  territory: [],
  role: [DEFAULT_EFFORTS_ROLE],
  tenureBand: [],
});

export default function EffortsFilterBar() {
  const { applied, applySelection, resetSelection } = useEffortsFilterState();
  const { fiscalYears, isLoading: periodsLoading } = useEffortsPeriods();

  // ── URL ownership: read/write ONLY the Efforts-owned keys. Writing from a
  // fresh URLSearchParams deletes any foreign param. The store (not the URL) is
  // the applied source of truth; this bridges the two. ──────────────────────
  const { readParam, writeOwnParams } = useUrlFilters(EFFORTS_FILTER_KEYS);

  // Draft (pre-Apply) selection, seeded from the applied selection. The `year`
  // (fiscal year) is a bar-local helper that narrows the Period list; it is not
  // part of the store, so it seeds from the URL `fy` on first render.
  const [draft, setDraft] = useState(() => ({
    ...emptyDraft(),
    year: readParam('fy'),
    yearmonth: applied.yearmonth ?? null,
    division: [...applied.division],
    state: [...applied.state],
    territory: [...applied.territory],
    role: [...applied.role],
    tenureBand: [...applied.tenureBand],
  }));

  // Re-seed the draft when the applied selection changes externally (e.g.
  // Reset). The Division auto-commit (handleDivisionClose) changes `applied`
  // itself, but it has already set the matching draft fields, so it flags the
  // next re-seed to be skipped — otherwise it would clobber any pending draft
  // edits to role/tenure/year/period that are meant to apply only on Apply.
  const lastAppliedRef = useRef(applied);
  const skipNextReseedRef = useRef(false);
  if (lastAppliedRef.current !== applied) {
    lastAppliedRef.current = applied;
    if (skipNextReseedRef.current) {
      skipNextReseedRef.current = false;
    } else {
      setDraft((prev) => ({
        ...prev,
        yearmonth: applied.yearmonth ?? null,
        division: [...applied.division],
        state: [...applied.state],
        territory: [...applied.territory],
        role: [...applied.role],
        tenureBand: [...applied.tenureBand],
      }));
    }
  }

  // Cascading option lists, narrowed by the DRAFT yearmonth/division/state.
  const {
    divisions,
    states,
    territories,
    divisionStats,
    stateStats,
    territoryStats,
    divisionsLoading,
    statesLoading,
    territoriesLoading,
  } = useEffortsFilterOptions({
    yearmonth: draft.yearmonth,
    division: draft.division,
    state: draft.state,
  });

  // ── Year / Period options from /periods ──
  const yearOptions = useMemo(
    () => [
      { value: null, label: 'All' },
      ...fiscalYears.map((fy) => ({ value: fy.fiscalYear, label: fy.fiscalYear })),
    ],
    [fiscalYears]
  );

  const periodOptions = useMemo(() => {
    // When a year is picked, show only that FY's months; otherwise all months.
    const source = draft.year
      ? fiscalYears.filter((fy) => fy.fiscalYear === draft.year)
      : fiscalYears;
    const months = source.flatMap((fy) => fy.months ?? []);
    // `null` keeps the backend's "latest month" default; shown as "All".
    return [
      { value: null, label: 'All' },
      ...months.map((m) => ({ value: m.yearmonth, label: m.label })),
    ];
  }, [fiscalYears, draft.year]);

  // Drop a selected period that no longer belongs to the selected year.
  useEffect(() => {
    if (!draft.yearmonth) return;
    const valid = periodOptions.some((o) => o.value === draft.yearmonth);
    if (!valid) {
      setDraft((prev) => ({ ...prev, yearmonth: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.year]);

  // ── Division validation: drop URL-hydrated divisions not in the options ────
  // Divisions are always the full list (not narrowed), so once they load, any
  // selected division absent from the list is invalid; dropping it also clears
  // its dependents (state/HQ). Mirrors the other pages' "clear value + deps".
  // Only acts when it actually changes the selection, so it never fights the
  // user. If this clears the APPLIED division too, re-apply so data matches.
  useEffect(() => {
    if (divisionsLoading || divisions.length === 0) return;
    setDraft((prev) => {
      if (prev.division.length === 0) return prev;
      const nextDivision = prev.division.filter((d) => divisions.includes(d));
      if (sameSelection(nextDivision, prev.division)) return prev;
      // A division was invalid → clear it and its dependents in the draft, and
      // commit the division-level change (like handleDivisionClose) so the page
      // refetches with a valid selection.
      skipNextReseedRef.current = true;
      applySelection({ ...applied, division: nextDivision, state: [], territory: [] });
      return { ...prev, division: nextDivision, state: [], territory: [] };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisions, divisionsLoading]);

  // ── Downstream cascade pruning: drop invalid state/territory values ──
  useEffect(() => {
    if (statesLoading || territoriesLoading) return;
    setDraft((prev) => {
      const nextState = prev.state.filter((s) => states.includes(s));
      const nextTerritory = prev.territory.filter((t) => territories.includes(t));
      if (
        sameSelection(nextState, prev.state) &&
        sameSelection(nextTerritory, prev.territory)
      ) {
        return prev;
      }
      return { ...prev, state: nextState, territory: nextTerritory };
    });
  }, [states, territories, statesLoading, territoriesLoading]);

  const setField = (field) => (next) => setDraft((prev) => ({ ...prev, [field]: next }));

  const setDivision = (next) => setDraft((prev) => ({ ...prev, division: next }));
  const setState = (next) => setDraft((prev) => ({ ...prev, state: next }));

  /**
   * DIVISION auto-apply: fired when the Division dropdown CLOSES. If the draft
   * division differs from the applied division, commit ONLY the division
   * selection immediately (so the page refetches without Apply), clearing the
   * dependent State/HQ selections in both draft and applied, and keeping every
   * other applied value (yearmonth, role, tenureBand) unchanged. No other draft
   * value is committed here. If the division is unchanged, do nothing.
   */
  const handleDivisionClose = () => {
    if (sameSelection(draft.division, applied.division)) return;
    // Clear dependent selections in the draft too, so draft matches applied and
    // the Apply button does not light up for the division change alone. We
    // already updated the draft here, so skip the re-seed that our own
    // applySelection() will trigger (it must not revert pending draft edits).
    skipNextReseedRef.current = true;
    setDraft((prev) => ({ ...prev, state: [], territory: [] }));
    applySelection({
      ...applied,
      division: draft.division,
      state: [],
      territory: [],
    });
  };

  const setYear = (next) =>
    setDraft((prev) => ({
      ...prev,
      year: next,
      // Clearing the year keeps the period; picking a year revalidates via effect.
    }));

  const dirty =
    !sameSelection(draft.division, applied.division) ||
    !sameSelection(draft.state, applied.state) ||
    !sameSelection(draft.territory, applied.territory) ||
    (draft.yearmonth ?? null) !== (applied.yearmonth ?? null) ||
    !sameSelection(draft.role, applied.role) ||
    !sameSelection(draft.tenureBand, applied.tenureBand);

  // Role is at default when it's exactly the single default designation.
  const isDefaultRole = (role) =>
    role.length === 1 && role[0] === DEFAULT_EFFORTS_ROLE;

  const isDefault =
    draft.division.length === 0 &&
    draft.state.length === 0 &&
    draft.territory.length === 0 &&
    draft.yearmonth == null &&
    draft.tenureBand.length === 0 &&
    isDefaultRole(draft.role) &&
    applied.division.length === 0 &&
    applied.state.length === 0 &&
    applied.territory.length === 0 &&
    applied.yearmonth == null &&
    applied.tenureBand.length === 0 &&
    isDefaultRole(applied.role);

  const handleApply = () =>
    applySelection({
      division: draft.division,
      state: draft.state,
      territory: draft.territory,
      yearmonth: draft.yearmonth,
      role: draft.role,
      tenureBand: draft.tenureBand,
    });

  const handleReset = () => {
    setDraft(emptyDraft());
    resetSelection();
  };

  // ── On mount: hydrate the store from the URL's own keys (once) ─────────────
  // If the URL carries NONE of the Efforts keys, do nothing — the store keeps
  // its default (role FLS), exactly as before. Otherwise, build a validated
  // selection and apply it so data loads on page load. Statically-validatable
  // fields (role/tenure against the enums, month shape) are validated here;
  // division/state/hq are applied as given and pruned against the loaded option
  // lists by the cascade-pruning effects below (invalid values + dependents are
  // dropped, placeholders show). `fy` seeds the draft year only (not a filter).
  const didHydrateRef = useRef(false);
  useEffect(() => {
    if (didHydrateRef.current) return;
    didHydrateRef.current = true;

    const fy = readParam('fy');
    const monthUrl = readParam('month');
    const division = splitList(readParam('division'));
    const state = splitList(readParam('state'));
    const hq = splitList(readParam('hq'));
    const roleRaw = splitList(readParam('role')).filter((r) => ROLE_OPTIONS.includes(r));
    const tenure = splitList(readParam('tenure')).filter((t) => TENURE_OPTIONS.includes(t));

    const hasAny =
      fy != null ||
      monthUrl != null ||
      division.length > 0 ||
      state.length > 0 ||
      hq.length > 0 ||
      roleRaw.length > 0 ||
      tenure.length > 0;
    if (!hasAny) return;

    const next = {
      division,
      state,
      territory: hq,
      yearmonth: monthUrlToWire(monthUrl),
      // Role defaults to FLS when the URL supplies none valid (matches the
      // store/empty default), so a bookmarked URL never applies an empty role.
      role: roleRaw.length ? roleRaw : [DEFAULT_EFFORTS_ROLE],
      tenureBand: tenure,
    };

    // Keep the draft in sync with what we apply so the Apply button starts
    // un-dirtied, and seed the bar-local year from `fy`.
    setDraft((prev) => ({ ...prev, ...next, year: fy }));
    skipNextReseedRef.current = true;
    applySelection(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Write the URL whenever the APPLIED selection changes ───────────────────
  // Only the Efforts-owned keys are written (empties omitted), from a fresh
  // URLSearchParams, so foreign params are dropped. Month is converted back to
  // the shared YYYY-MM URL form. `fy` reflects the bar-local year selection
  // (it narrows Period only and is not stored in `applied`).
  useEffect(() => {
    writeOwnParams({
      fy: draft.year,
      month: monthWireToUrl(applied.yearmonth),
      division: joinList(applied.division),
      state: joinList(applied.state),
      hq: joinList(applied.territory),
      // Omit role when it's the lone default so a default landing stays clean.
      role:
        applied.role.length === 1 && applied.role[0] === DEFAULT_EFFORTS_ROLE
          ? null
          : joinList(applied.role),
      tenure: joinList(applied.tenureBand),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied, draft.year]);

  return (
    <Paper variant="outlined" sx={FILTER_BAR_PAPER_SX}>
      <Box
        sx={{
          display: 'flex',
          gap: FILTER_BAR_GAP,
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            gap: FILTER_BAR_GAP,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            minWidth: 0,
            // Compact wrapping grid (matches Division Performance): every
            // control sits on a fixed ~180px basis and wraps onto the next
            // line, rather than each taking a full-width row (the shared
            // field wrapper is width:100%). Scoped to this bar.
            '& > *': { flex: '1 1 180px', minWidth: 180, maxWidth: 260 },
          }}
        >
          <SingleSelectFilter
            label="Year"
            value={draft.year}
            options={yearOptions}
            loading={periodsLoading}
            onChange={setYear}
            placeholder="All"
          />
          <SingleSelectFilter
            label="Period"
            value={draft.yearmonth}
            options={periodOptions}
            loading={periodsLoading}
            onChange={setField('yearmonth')}
            placeholder="All"
          />
          <MultiSelectFilter
            label="Division"
            value={draft.division}
            options={divisions}
            stats={divisionStats}
            loading={divisionsLoading}
            onChange={setDivision}
            onClose={handleDivisionClose}
          />
          <MultiSelectFilter
            label="State"
            value={draft.state}
            options={states}
            stats={stateStats}
            loading={statesLoading}
            onChange={setState}
          />
          <MultiSelectFilter
            label="HQ / Territory"
            value={draft.territory}
            options={territories}
            stats={territoryStats}
            loading={territoriesLoading}
            onChange={setField('territory')}
          />
          {SHOW_LOCKED_FILTERS ? (
            <>
              <LockedFilter label="Brand" />
              <LockedFilter label="BDM" />
              <LockedFilter label="ZM" />
            </>
          ) : null}
          <MultiSelectFilter
            label="Designation"
            value={draft.role}
            options={ROLE_OPTIONS}
            onChange={setField('role')}
          />
          <MultiSelectFilter
            label="Tenure Band"
            value={draft.tenureBand}
            options={TENURE_OPTIONS}
            onChange={setField('tenureBand')}
            getOptionLabel={(o) => TENURE_LABELS[o] ?? o}
          />
          {/* Apply + Reset sit in the same wrapping row, right next to the
              Tenure Band filter. They opt out of the fixed ~180px control
              basis so they stay compact. */}
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              alignItems: 'flex-end',
              flex: '0 0 auto',
              minWidth: 'auto',
              maxWidth: 'none',
            }}
          >
            <FilterApplyButton onClick={handleApply} disabled={!dirty} />
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              disabled={isDefault}
              sx={FILTER_BUTTON_SX}
            >
              Reset
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
